import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { DropdownMenu, DropdownToggle, FormGroup, Dropdown } from 'src/components/ui';
import Icon from '../Icon';
import { t } from '@lingui/core/macro';
import Notification from '../Notification';
import ElementIcon from '../ElementIcon';
import Loader from '../Loader';
import useLoader from '../../util/useLoader';
import faIcons from '../../util/icons/faIcons';
import duotoneIcons from '../../util/icons/duotoneIcons';
import { REACT_ICON_PACKS, listPackIcons, parseReactIconName } from '../../util/icons/reactIconPacks';
import useTestId, { getTestId } from '../../hooks/useTestId';

// ── helpers ──────────────────────────────────────────────────────────────────

// Library selector value for the built-in Font Awesome + Phosphor duotone view.
const DEFAULT_LIBRARY = 'default';
// Big libraries (Phosphor has ~9k icons) render in pages as the grid scrolls.
const RENDER_PAGE_SIZE = 400;
const SCROLL_LOAD_THRESHOLD_PX = 120;

function computeKey(item) {
  if (!item) return 'none';
  const { icon, icons, image } = item;
  return icon ? `icon.${icon}` : icons ? `icons.${icons.base}` : `image.${image}`;
}

/** Human-readable label used for search + tooltip */
function iconLabel(item) {
  if (!item?.icon) return '';
  const icon = item.icon;
  const faMatch = icon.match(/fa-([a-z0-9-]+)/);
  if (faMatch) return faMatch[1];
  const reactIconRef = parseReactIconName(icon);
  const name = reactIconRef ? icon.slice(reactIconRef.pack.alias.length + 1) : icon;
  // PascalCase → words, e.g. "ChevronDown" → "chevron down"
  return name
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase();
}

// ── main component ────────────────────────────────────────────────────────────

function SelectImageField(props) {
  const {
    formData,
    schema: { title },
    fieldPathId: { $id, path },
  } = props;
  const { onChange } = props;
  const testIdProps = useTestId($id);

  const [isOpen, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen(v => !v), []);

  const onSelectIcon = useCallback(
    icon => {
      onChange(icon, path);
      setOpen(false);
    },
    [onChange, path]
  );

  return (
    <FormGroup className="select-image-field">
      {title !== ' ' && (
        <label className="control-label" htmlFor={$id}>
          {title}
        </label>
      )}
      <div {...testIdProps}>
        <Dropdown isOpen={isOpen} toggle={toggle}>
          <DropdownToggle className="simple-dropdown-toggle" caret {...getTestId($id, 'toggle')}>
            <ElementIcon element={formData} size={40} />
          </DropdownToggle>
          {isOpen && (
            <DropdownMenu container="body" style={{ padding: 0, width: 540, maxWidth: 'min(95vw, 640px)' }}>
              <IconPicker selectedIcon={formData} onSelectIcon={onSelectIcon} />
            </DropdownMenu>
          )}
        </Dropdown>
      </div>
    </FormGroup>
  );
}

// ── icon picker ───────────────────────────────────────────────────────────────

function loadDefaultIcons() {
  return Promise.all([
    faIcons.getList ? faIcons.getList() : Promise.resolve(faIcons.items || []),
    duotoneIcons.getList ? duotoneIcons.getList() : Promise.resolve(duotoneIcons.items || []),
  ]).then(([faList, duotoneList]) => [
    ...faList.map(item => ({ ...item, _pack: 'fa' })),
    ...duotoneList.map(item => ({ ...item, _pack: 'duotone' })),
  ]);
}

function loadLibraryIcons(library) {
  const pack = REACT_ICON_PACKS.find(p => p.alias === library);
  return pack ? listPackIcons(pack) : loadDefaultIcons();
}

function IconPicker({ selectedIcon, onSelectIcon }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allIcons, setAllIcons] = useState([]);
  // Open on the library of the current icon so it is visible and highlighted
  const [library, setLibrary] = useState(() => parseReactIconName(selectedIcon?.icon)?.pack.alias ?? DEFAULT_LIBRARY);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadLibraryIcons(library)
      .then(icons => {
        if (cancelled) return;
        setAllIcons(icons);
        setLoading(false);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [library]);

  const [query, setQuery] = useState('');
  // Both packs shown by default; clicking a chip toggles it
  const [filters, setFilters] = useState({ fa: true, duotone: true });

  const toggleFilter = useCallback(pack => {
    setFilters(prev => ({ ...prev, [pack]: !prev[pack] }));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allIcons.filter(item => {
      if (item._pack === 'fa' && !filters.fa) return false;
      if (item._pack === 'duotone' && !filters.duotone) return false;
      if (!q) return true;
      return iconLabel(item).includes(q);
    });
  }, [allIcons, filters, query]);

  const [renderCount, setRenderCount] = useState(RENDER_PAGE_SIZE);
  useEffect(() => setRenderCount(RENDER_PAGE_SIZE), [filtered]);

  const onGridScroll = useCallback(
    e => {
      const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
      if (renderCount < filtered.length && scrollTop + clientHeight >= scrollHeight - SCROLL_LOAD_THRESHOLD_PX) {
        setRenderCount(count => count + RENDER_PAGE_SIZE);
      }
    },
    [renderCount, filtered.length]
  );

  const selectedKey = computeKey(selectedIcon);

  return (
    <>
      {/* Scoped hover styles — avoids inline onMouseEnter/Leave on every cell */}
      <style>{`
        .sif-grid-item {
          display:flex; align-items:center; justify-content:center;
          width:36px; height:36px;
          border:none; background:transparent; border-radius:6px;
          cursor:pointer; color:#757575;
          transition:background 120ms, color 120ms;
          flex-shrink:0;
        }
        .sif-grid-item:hover { background:#f5f5f5; color:var(--app-primary,#0d6efd); }
        .sif-grid-item.sif-selected {
          background:color-mix(in srgb,var(--app-primary,#0d6efd) 12%,transparent);
          color:var(--app-primary,#0d6efd);
        }
        .sif-chip {
          display:inline-flex; align-items:center; gap:4px;
          padding:3px 10px; border-radius:999px; font-size:0.75rem;
          font-weight:600; border:1.5px solid; cursor:pointer;
          transition:background 120ms, color 120ms, border-color 120ms;
          white-space:nowrap;
        }
        .sif-chip-on  { background:var(--app-primary,#0d6efd); color:#fff; border-color:var(--app-primary,#0d6efd); }
        .sif-chip-off { background:transparent; color:#9e9e9e; border-color:#e0e0e0; }
        .sif-chip-off:hover { border-color:#9e9e9e; color:#616161; }
        .sif-search-row:focus-within { border-bottom-color:var(--app-primary,#0d6efd) !important; }
        .sif-search-row:focus-within .sif-search-icon { color:var(--app-primary,#0d6efd); }
        .sif-library {
          max-width:150px; padding:2px 6px; border-radius:6px; font-size:0.75rem;
          border:1.5px solid #e0e0e0; background:transparent; color:#616161; cursor:pointer;
        }
        .sif-library:focus { outline:none; border-color:var(--app-primary,#0d6efd); }
      `}</style>

      {/* Row 1: search + pack filter chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: '1px solid #f0f0f0' }}>
        {/* Search input — bottom-border only with icon decorator */}
        <div
          className="sif-search-row"
          style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1.5px solid #e0e0e0', paddingBottom: 2 }}
        >
          <Icon icon="MagnifyingGlass" className="sif-search-icon" size={14} style={{ color: '#bdbdbd', flexShrink: 0, transition: 'color 120ms' }} />
          <input
            type="text"
            value={query}
            placeholder={t`Search icons…`}
            onChange={e => setQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: '0.85rem',
              color: '#212121',
              padding: 0,
              lineHeight: '1.4',
            }}
          />
        </div>
        <select className="sif-library" value={library} onChange={e => setLibrary(e.target.value)} title={t`Icon library`}>
          <option value={DEFAULT_LIBRARY}>{t`Phosphor Duotone & Font Awesome`}</option>
          {REACT_ICON_PACKS.map(pack => (
            <option key={pack.alias} value={pack.alias}>
              {pack.title}
            </option>
          ))}
        </select>
        {library === DEFAULT_LIBRARY && (
          <>
            <button
              type="button"
              className={`sif-chip ${filters.fa ? 'sif-chip-on' : 'sif-chip-off'}`}
              onClick={() => toggleFilter('fa')}
              title="Toggle Font Awesome icons"
            >
              FA
            </button>
            <button
              type="button"
              className={`sif-chip ${filters.duotone ? 'sif-chip-on' : 'sif-chip-off'}`}
              onClick={() => toggleFilter('duotone')}
              title="Toggle Phosphor duotone icons"
            >
              Duotone
            </button>
          </>
        )}
      </div>

      {/* Row 2: icon grid */}
      <div style={{ height: 260, overflowY: 'auto', padding: '8px 10px' }} onScroll={onGridScroll}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Loader />
          </div>
        ) : error ? (
          <Notification error={error} />
        ) : filtered.length === 0 ? (
          <div style={{ color: '#9e9e9e', textAlign: 'center', paddingTop: 40, fontSize: '0.85rem' }}>{t`No icons found`}</div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 36px)',
              gap: 4,
            }}
          >
            {filtered.slice(0, renderCount).map(item => {
              const key = computeKey(item);
              const selected = key === selectedKey;
              return (
                <button
                  key={key}
                  type="button"
                  className={`sif-grid-item${selected ? ' sif-selected' : ''}`}
                  title={iconLabel(item)}
                  onClick={() => onSelectIcon(item)}
                >
                  <ElementIcon element={item} fontSize={18} />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer: count */}
      <div
        style={{
          borderTop: '1px solid #f0f0f0',
          padding: '4px 10px',
          fontSize: '0.7rem',
          color: '#9e9e9e',
          textAlign: 'right',
        }}
      >
        {filtered.length.toLocaleString()} icons
      </div>
    </>
  );
}

export { IconPicker };
export default SelectImageField;
